import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, Trash2 } from 'lucide-react';
import TastingRoomInventoryListToolbar from '../components/toolbars/TastingRoomInventoryListToolbar';
import '../index.css';

const TastingRoomInventoryMaster = () => {
    const [logs, setLogs] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [error, setError] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedLogID, setSelectedLogID] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [logsResponse, locationsResponse] = await Promise.all([
                    axios.get('/api/tasting-room/inventory-logs'),
                    axios.get('/api/tasting-room/locations')
                ]);
                
                setLogs(logsResponse.data);
                setLocations(locationsResponse.data);
                setIsLoading(false);
            } catch (err) {
                console.error('Error fetching initial data:', err);
                setError('Failed to load inventory logs');
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, []);

const handleCreateLog = async () => {
    if (!selectedLocation) {
        alert('Please select a location first');
        return;
    }

    setIsCreating(true);
    try {
        // First navigate to the new inventory page
        navigate('/tasting-room/inventory/new');
    } catch (err) {
        console.error('Error creating inventory log:', err);
        setError('Failed to create new inventory log');
        setIsCreating(false);
    }
};

    const handleRowClick = (logID) => {
        navigate(`/tasting-room/inventory-logs/${logID}`);
    };

    const handleDeleteClick = (e, logID) => {
        e.stopPropagation();
        setSelectedLogID(logID);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axios.delete(`/api/tasting-room/inventory-logs/${selectedLogID}`);
            setLogs(logs.filter(log => log.logID !== selectedLogID));
            setShowDeleteModal(false);
            setSelectedLogID(null);
        } catch (err) {
            console.error('Error deleting inventory log:', err);
            setError('Failed to delete inventory log');
        }
    };

    if (isLoading) {
        return (
            <>
                <TastingRoomInventoryListToolbar 
                    locations={locations}
                    selectedLocation={selectedLocation}
                    onLocationChange={setSelectedLocation}
                    onCreateLog={handleCreateLog}
                    isCreating={isCreating}
                />
                <div className="container mt-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <TastingRoomInventoryListToolbar 
                locations={locations}
                selectedLocation={selectedLocation}
                onLocationChange={setSelectedLocation}
                onCreateLog={handleCreateLog}
                isCreating={isCreating}
            />
            
            <div className="container mt-4">
                {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {error}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setError(null)}
                        ></button>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Location</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.length > 0 ? (
                                        logs.map(log => (
                                            <tr 
                                                key={log.logID}
                                                onClick={() => handleRowClick(log.logID)}
                                                style={{ cursor: 'pointer' }}
                                                className="table-row-hover"
                                            >
                                                <td>{new Date(log.logDate).toLocaleDateString()}</td>
                                                <td>{log.locationName}</td>
                                                <td>
                                                    <span className="badge bg-success">Completed</span>
                                                </td>
                                                <td className="text-end">
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/tasting-room-inventory/${log.logID}`);
                                                            }}
                                                            title="View Details"
                                                        >
                                                            <Eye size={16} />
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={(e) => handleDeleteClick(e, log.logID)}
                                                            title="Delete Log"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center">
                                                No inventory logs available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <>
                        <div className="modal-backdrop fade show"></div>
                        <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Confirm Delete</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setShowDeleteModal(false)}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <p>Are you sure you want to delete this inventory log? This action cannot be undone.</p>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setShowDeleteModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger"
                                            onClick={handleDeleteConfirm}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <style jsx>{`
                    .table-row-hover:hover td {
                        background-color: rgba(0,0,0,.075);
                    }
                    .table-row-hover {
                        transition: background-color 0.2s ease;
                    }
                `}</style>
            </div>
        </>
    );
};

export default TastingRoomInventoryMaster;